import { API } from "@/config/apiBase";
import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Users, TrendingUp, DollarSign, Gift, Ticket, Share2, 
  Plus, Trash2, Copy, Clock, Check, ArrowLeft, Crown,
  Phone, Calendar, Shield, RefreshCw, Info, BarChart3
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { toast } from "sonner";
import axios from "axios";
import { AuthContext } from "@/App";


const AdminDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState("user");
  const [isLoading, setIsLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [inviteCodes, setInviteCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [roleUpdating, setRoleUpdating] = useState({});
  const [showCreateCode, setShowCreateCode] = useState(false);
  const [newCode, setNewCode] = useState({
    code: "",
    tier: "standard",
    max_uses: "",
    expires_days: 30,
    description: ""
  });
  
  // Billing debug tools state
  const [showExpiryDialog, setShowExpiryDialog] = useState(false);
  const [selectedUserForExpiry, setSelectedUserForExpiry] = useState(null);
  const [expiryDate, setExpiryDate] = useState("");
  const [expiryReason, setExpiryReason] = useState("");
  const [isSettingExpiry, setIsSettingExpiry] = useState(false);
  const [isRunningExpiryJob, setIsRunningExpiryJob] = useState(false);

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const response = await axios.get(`${API}/admin/check`);
      if (response.data.is_admin) {
        setIsAdmin(true);
        setCurrentUserRole(response.data.role || "admin");
        fetchAllData();
      } else {
        toast.error("Access denied. Admin only.");
        navigate("/dashboard");
      }
    } catch (error) {
      navigate("/dashboard");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllData = async () => {
    try {
      const [analyticsRes, codesRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/analytics`),
        axios.get(`${API}/admin/invite-codes`),
        axios.get(`${API}/admin/users?limit=20`)
      ]);
      setAnalytics(analyticsRes.data);
      setInviteCodes(codesRes.data);
      setUsers(usersRes.data.users);
    } catch (error) {
      toast.error("Failed to load admin data");
    }
  };

  const handleCreateCode = async (e) => {
    e.preventDefault();
    try {
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + parseInt(newCode.expires_days || 30));
      
      const response = await axios.post(`${API}/admin/invite-codes`, {
        code: newCode.code || null,
        tier: newCode.tier,
        reward_type: "free_month",
        reward_value: newCode.tier === "seed" ? 6 : newCode.tier === "vip" ? 3 : newCode.tier === "premium" ? 2 : 1,
        max_uses: newCode.max_uses ? parseInt(newCode.max_uses) : null,
        expires_at: expiresAt.toISOString(),
        description: newCode.description
      });
      
      toast.success(`Created code: ${response.data.code}`);
      setShowCreateCode(false);
      setNewCode({ code: "", tier: "standard", max_uses: "", expires_days: 30, description: "" });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create code");
    }
  };

  const handleDeleteCode = async (code) => {
    if (!confirm(`Deactivate code ${code}?`)) return;
    try {
      await axios.delete(`${API}/admin/invite-codes/${code}`);
      toast.success("Code deactivated");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to deactivate code");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Code copied!");
  };

  const handleUpdateUserPlan = async (userId, plan) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/plan?plan=${plan}&months=1`);
      toast.success("User plan updated");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    // Prevent changing own role
    if (userId === user?.user_id) {
      toast.error("You can't change your own role");
      return;
    }

    setRoleUpdating(prev => ({ ...prev, [userId]: true }));
    
    try {
      const response = await axios.put(`${API}/admin/users/${userId}/role`, { role: newRole });
      toast.success(response.data.message || `Role updated to ${newRole}`);
      
      // Optimistically update the users list
      setUsers(prev => prev.map(u => 
        u.user_id === userId ? { ...u, role: newRole, is_admin: newRole !== "user" } : u
      ));
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to update role";
      toast.error(message);
    } finally {
      setRoleUpdating(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Get effective role for a user (handles legacy is_admin flag)
  const getUserRole = (u) => {
    if (u.role) return u.role;
    if (u.is_admin) return "admin";
    return "user";
  };

  // Handle setting subscription expiry date for a user
  const handleSetExpiry = async () => {
    if (!selectedUserForExpiry || !expiryDate) {
      toast.error("Please select a date");
      return;
    }

    setIsSettingExpiry(true);
    try {
      // Convert datetime-local to ISO string
      const isoDate = new Date(expiryDate).toISOString();
      
      await axios.put(`${API}/admin/users/${selectedUserForExpiry.user_id}/subscription-end`, {
        subscription_end: isoDate,
        reason: expiryReason || "Admin debug tool"
      });
      
      toast.success(`Expiry date set for ${selectedUserForExpiry.email}`);
      setShowExpiryDialog(false);
      setSelectedUserForExpiry(null);
      setExpiryDate("");
      setExpiryReason("");
      fetchAllData(); // Refresh user list
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to set expiry date";
      toast.error(message);
    } finally {
      setIsSettingExpiry(false);
    }
  };

  // Run subscription expiry job manually
  const handleRunExpiryJob = async () => {
    if (!confirm("Run the expiry job? This will downgrade all expired comped users to free.")) {
      return;
    }

    setIsRunningExpiryJob(true);
    try {
      const response = await axios.post(`${API}/admin/run-expiry-job`);
      const result = response.data;
      
      if (result.expired_users_found === 0) {
        toast.success("No expired users found");
      } else {
        toast.success(`Processed ${result.expired_users_found} expired users. ${result.downgraded?.length || 0} downgraded.`);
      }
      
      fetchAllData(); // Refresh data
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to run expiry job";
      toast.error(message);
    } finally {
      setIsRunningExpiryJob(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#1a1a2e] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#E07A5F] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <TooltipProvider>
    <div className="min-h-screen bg-[#1a1a2e] text-white">
      {/* Header */}
      <header className="bg-[#16162a] border-b border-[#2a2a4a] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 rounded-full">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <Shield className="w-6 h-6 text-[#E07A5F]" />
                <h1 className="text-xl font-bold" style={{ fontFamily: 'Fraunces, serif' }}>
                  Admin Dashboard
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/admin/analytics/events">
                <Button 
                  variant="outline" 
                  className="border-[#2a2a4a] text-white hover:bg-white/10"
                  data-testid="events-stream-link"
                >
                  <BarChart3 className="w-4 h-4 mr-2" /> Events Stream
                </Button>
              </Link>
              <Button 
                onClick={handleRunExpiryJob} 
                variant="outline" 
                disabled={isRunningExpiryJob}
                className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300"
                data-testid="run-expiry-job-btn"
              >
                {isRunningExpiryJob ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Running...
                  </>
                ) : (
                  <>
                    <Clock className="w-4 h-4 mr-2" /> Run Expiry Job
                  </>
                )}
              </Button>
              <Button onClick={fetchAllData} variant="ghost" className="text-white hover:bg-white/10">
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {analytics && (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <motion.div 
                className="bg-[#16162a] rounded-2xl p-6 border border-[#2a2a4a]"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <Users className="w-5 h-5 text-[#81B29A]" />
                  <span className="text-[#8a8aa3] text-sm">Total Users</span>
                </div>
                <p className="text-3xl font-bold">{analytics.users.total}</p>
                <p className="text-xs text-[#81B29A]">+{analytics.users.this_month} this month</p>
              </motion.div>

              <motion.div 
                className="bg-[#16162a] rounded-2xl p-6 border border-[#2a2a4a]"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <Crown className="w-5 h-5 text-[#F2CC8F]" />
                  <span className="text-[#8a8aa3] text-sm">Paid Users</span>
                </div>
                <p className="text-3xl font-bold">{analytics.subscriptions.plus + analytics.subscriptions.family}</p>
                <p className="text-xs text-[#F2CC8F]">{analytics.subscriptions.conversion_rate}% conversion</p>
              </motion.div>

              {/* Forward MRR Card */}
              <motion.div 
                className="bg-[#16162a] rounded-2xl p-6 border border-[#2a2a4a]"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <DollarSign className="w-5 h-5 text-[#81B29A]" />
                  <span className="text-[#8a8aa3] text-sm">Forward MRR</span>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="w-3 h-3 text-[#8a8aa3]" />
                    </TooltipTrigger>
                    <TooltipContent className="bg-[#1a1a2e] border-[#2a2a4a] text-white max-w-xs">
                      <p className="text-xs">{analytics.revenue?.note_forward || "Expected to renew next month"}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <p className="text-3xl font-bold">${analytics.revenue?.forward_mrr || 0}</p>
                <p className="text-xs text-[#8a8aa3]">
                  Plus: ${analytics.revenue?.forward_mrr_breakdown?.plus || 0} | Family: ${analytics.revenue?.forward_mrr_breakdown?.family || 0}
                </p>
              </motion.div>

              {/* Active MRR Card */}
              <motion.div 
                className="bg-[#16162a] rounded-2xl p-6 border border-[#2a2a4a]"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <DollarSign className="w-5 h-5 text-[#E07A5F]" />
                  <span className="text-[#8a8aa3] text-sm">Active MRR</span>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="w-3 h-3 text-[#8a8aa3]" />
                    </TooltipTrigger>
                    <TooltipContent className="bg-[#1a1a2e] border-[#2a2a4a] text-white max-w-xs">
                      <p className="text-xs">{analytics.revenue?.note_active || "Currently billed (includes canceling)"}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <p className="text-3xl font-bold">${analytics.revenue?.active_mrr || 0}</p>
                <p className="text-xs text-[#8a8aa3]">
                  Plus: ${analytics.revenue?.active_mrr_breakdown?.plus || 0} | Family: ${analytics.revenue?.active_mrr_breakdown?.family || 0}
                </p>
              </motion.div>

              <motion.div 
                className="bg-[#16162a] rounded-2xl p-6 border border-[#2a2a4a]"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <Phone className="w-5 h-5 text-[#E07A5F]" />
                  <span className="text-[#8a8aa3] text-sm">Calls This Month</span>
                </div>
                <p className="text-3xl font-bold">{analytics.engagement.calls_this_month}</p>
                <p className="text-xs text-[#8a8aa3]">{analytics.engagement.total_calls} total</p>
              </motion.div>
            </div>

            {/* Secondary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-[#16162a] rounded-xl p-4 border border-[#2a2a4a]">
                <p className="text-[#8a8aa3] text-xs mb-1">Total Contacts</p>
                <p className="text-xl font-bold">{analytics.engagement.total_contacts}</p>
              </div>
              <div className="bg-[#16162a] rounded-xl p-4 border border-[#2a2a4a]">
                <p className="text-[#8a8aa3] text-xs mb-1">Avg Contacts/User</p>
                <p className="text-xl font-bold">{analytics.engagement.avg_contacts_per_user}</p>
              </div>
              <div className="bg-[#16162a] rounded-xl p-4 border border-[#2a2a4a]">
                <p className="text-[#8a8aa3] text-xs mb-1">Gifts Sent</p>
                <p className="text-xl font-bold">{analytics.gifts.total}</p>
              </div>
              <div className="bg-[#16162a] rounded-xl p-4 border border-[#2a2a4a]">
                <p className="text-[#8a8aa3] text-xs mb-1">Referrals</p>
                <p className="text-xl font-bold">{analytics.referrals.total}</p>
              </div>
            </div>

            {/* Tabs Section */}
            <Tabs defaultValue="codes" className="w-full">
              <TabsList className="bg-[#16162a] border border-[#2a2a4a] rounded-xl p-1 mb-6">
                <TabsTrigger value="codes" className="data-[state=active]:bg-[#E07A5F] data-[state=active]:text-white rounded-lg">
                  <Ticket className="w-4 h-4 mr-2" /> Invite Codes
                </TabsTrigger>
                <TabsTrigger value="users" className="data-[state=active]:bg-[#E07A5F] data-[state=active]:text-white rounded-lg">
                  <Users className="w-4 h-4 mr-2" /> Users
                </TabsTrigger>
                <TabsTrigger value="referrals" className="data-[state=active]:bg-[#E07A5F] data-[state=active]:text-white rounded-lg">
                  <Share2 className="w-4 h-4 mr-2" /> Referrals
                </TabsTrigger>
              </TabsList>

              {/* Invite Codes Tab */}
              <TabsContent value="codes">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-semibold">Invite Codes ({inviteCodes.length})</h2>
                  <Button onClick={() => setShowCreateCode(true)} className="bg-[#E07A5F] hover:bg-[#D0694E] rounded-full">
                    <Plus className="w-4 h-4 mr-2" /> Create Code
                  </Button>
                </div>
                
                <div className="bg-[#16162a] rounded-2xl border border-[#2a2a4a] overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-[#0f0f1a]">
                      <tr>
                        <th className="text-left p-4 text-[#8a8aa3] text-sm font-medium">Code</th>
                        <th className="text-left p-4 text-[#8a8aa3] text-sm font-medium">Tier</th>
                        <th className="text-left p-4 text-[#8a8aa3] text-sm font-medium">Uses</th>
                        <th className="text-left p-4 text-[#8a8aa3] text-sm font-medium">Expires</th>
                        <th className="text-left p-4 text-[#8a8aa3] text-sm font-medium">Status</th>
                        <th className="text-right p-4 text-[#8a8aa3] text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inviteCodes.map((code, i) => (
                        <tr key={code.code_id} className="border-t border-[#2a2a4a]">
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <code className="bg-[#0f0f1a] px-2 py-1 rounded text-[#E07A5F]">{code.code}</code>
                              <Button variant="ghost" size="sm" onClick={() => copyCode(code.code)} className="text-white/50 hover:text-white p-1">
                                <Copy className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                          <td className="p-4">
                            <Badge className={
                              code.tier === "seed" ? "bg-purple-500/20 text-purple-400" :
                              code.tier === "vip" ? "bg-yellow-500/20 text-yellow-400" :
                              code.tier === "premium" ? "bg-blue-500/20 text-blue-400" :
                              "bg-gray-500/20 text-gray-400"
                            }>
                              {code.tier}
                            </Badge>
                          </td>
                          <td className="p-4 text-[#8a8aa3]">
                            {code.current_uses}/{code.max_uses || "∞"}
                          </td>
                          <td className="p-4 text-[#8a8aa3] text-sm">
                            {code.expires_at ? new Date(code.expires_at).toLocaleDateString() : "Never"}
                          </td>
                          <td className="p-4">
                            {code.is_active ? (
                              <Badge className="bg-green-500/20 text-green-400">Active</Badge>
                            ) : (
                              <Badge className="bg-red-500/20 text-red-400">Inactive</Badge>
                            )}
                          </td>
                          <td className="p-4 text-right">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleDeleteCode(code.code)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {inviteCodes.length === 0 && (
                        <tr>
                          <td colSpan={6} className="p-8 text-center text-[#8a8aa3]">
                            No invite codes yet. Create one to get started!
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </TabsContent>

              {/* Users Tab */}
              <TabsContent value="users">
                <h2 className="text-lg font-semibold mb-4">Recent Users ({users.length})</h2>
                
                <div className="bg-[#16162a] rounded-2xl border border-[#2a2a4a] overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-[#0f0f1a]">
                      <tr>
                        <th className="text-left p-4 text-[#8a8aa3] text-sm font-medium">User</th>
                        <th className="text-left p-4 text-[#8a8aa3] text-sm font-medium">Plan</th>
                        <th className="text-left p-4 text-[#8a8aa3] text-sm font-medium">Role</th>
                        <th className="text-left p-4 text-[#8a8aa3] text-sm font-medium">Joined</th>
                        <th className="text-right p-4 text-[#8a8aa3] text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => {
                        const userRole = getUserRole(u);
                        const isSelf = u.user_id === user?.user_id;
                        const canChangeToSuperAdmin = currentUserRole === "super_admin";
                        
                        return (
                          <tr key={u.user_id} className="border-t border-[#2a2a4a]">
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <div>
                                  <p className="font-medium flex items-center gap-2">
                                    {u.name}
                                    {isSelf && (
                                      <span className="text-xs text-[#8a8aa3]">(you)</span>
                                    )}
                                  </p>
                                  <p className="text-sm text-[#8a8aa3]">{u.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <Badge className={
                                  u.subscription_plan === "family" ? "bg-purple-500/20 text-purple-400" :
                                  u.subscription_plan === "plus" ? "bg-[#E07A5F]/20 text-[#E07A5F]" :
                                  "bg-gray-500/20 text-gray-400"
                                }>
                                  {u.subscription_plan || "free"}
                                </Badge>
                                {/* Comped badge for admin-granted subscriptions */}
                                {u.is_comped && (
                                  <Badge className="bg-violet-500/20 text-violet-400 text-xs">
                                    Comped
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td className="p-4">
                              {isSelf ? (
                                <div className="flex items-center gap-2">
                                  <Badge className={
                                    userRole === "super_admin" ? "bg-red-500/20 text-red-400" :
                                    userRole === "admin" ? "bg-amber-500/20 text-amber-400" :
                                    "bg-gray-500/20 text-gray-400"
                                  }>
                                    {userRole === "super_admin" ? "Super Admin" : 
                                     userRole === "admin" ? "Admin" : "User"}
                                  </Badge>
                                  <span className="text-xs text-[#8a8aa3]" title="You can't change your own role">
                                    (locked)
                                  </span>
                                </div>
                              ) : (
                                <Select 
                                  value={userRole} 
                                  onValueChange={(v) => handleUpdateUserRole(u.user_id, v)}
                                  disabled={roleUpdating[u.user_id]}
                                >
                                  <SelectTrigger className={`w-32 border-[#2a2a4a] text-white ${
                                    userRole === "super_admin" ? "bg-red-500/10" :
                                    userRole === "admin" ? "bg-amber-500/10" :
                                    "bg-[#0f0f1a]"
                                  }`}>
                                    <SelectValue>
                                      {roleUpdating[u.user_id] ? (
                                        <span className="text-[#8a8aa3]">Updating...</span>
                                      ) : (
                                        userRole === "super_admin" ? "Super Admin" : 
                                        userRole === "admin" ? "Admin" : "User"
                                      )}
                                    </SelectValue>
                                  </SelectTrigger>
                                  <SelectContent className="bg-[#16162a] border-[#2a2a4a]">
                                    <SelectItem value="user" className="text-white">User</SelectItem>
                                    <SelectItem value="admin" className="text-white">Admin</SelectItem>
                                    {canChangeToSuperAdmin && (
                                      <SelectItem value="super_admin" className="text-white">Super Admin</SelectItem>
                                    )}
                                  </SelectContent>
                                </Select>
                              )}
                            </td>
                            <td className="p-4 text-[#8a8aa3] text-sm">
                              {u.created_at ? new Date(u.created_at).toLocaleDateString() : "N/A"}
                            </td>
                            <td className="p-4 text-right">
                              <div className="flex items-center gap-2 justify-end">
                                {/* Show subscription_end if comped */}
                                {u.is_comped && u.subscription_end && (
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <span className="text-xs text-[#8a8aa3]">
                                        Ends: {new Date(u.subscription_end).toLocaleDateString()}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent className="bg-[#1a1a2e] border-[#2a2a4a] text-white">
                                      <p className="text-xs">{u.subscription_end}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                                <Select onValueChange={(v) => handleUpdateUserPlan(u.user_id, v)}>
                                  <SelectTrigger className="w-28 bg-[#0f0f1a] border-[#2a2a4a] text-white">
                                    <SelectValue placeholder="Plan" />
                                  </SelectTrigger>
                                  <SelectContent className="bg-[#16162a] border-[#2a2a4a]">
                                    <SelectItem value="free" className="text-white">Free</SelectItem>
                                    <SelectItem value="plus" className="text-white">Plus</SelectItem>
                                    <SelectItem value="family" className="text-white">Family</SelectItem>
                                  </SelectContent>
                                </Select>
                                {/* Set Expiry button for comped users */}
                                {u.is_comped && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-[#2a2a4a] text-[#8a8aa3] hover:text-white text-xs"
                                    onClick={() => {
                                      setSelectedUserForExpiry(u);
                                      setExpiryDate(u.subscription_end ? u.subscription_end.substring(0, 16) : "");
                                      setShowExpiryDialog(true);
                                    }}
                                  >
                                    <Clock className="w-3 h-3 mr-1" />
                                    Set Expiry
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </TabsContent>

              {/* Referrals Tab */}
              <TabsContent value="referrals">
                <h2 className="text-lg font-semibold mb-4">Top Referrers</h2>
                
                <div className="bg-[#16162a] rounded-2xl border border-[#2a2a4a] p-6">
                  {analytics.referrals.top_referrers.length > 0 ? (
                    <div className="space-y-4">
                      {analytics.referrals.top_referrers.map((r, i) => (
                        <div key={i} className="flex items-center justify-between p-4 bg-[#0f0f1a] rounded-xl">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-[#E07A5F]/20 rounded-full flex items-center justify-center text-[#E07A5F] font-bold">
                              #{i + 1}
                            </div>
                            <div>
                              <p className="font-medium">{r.name}</p>
                              <p className="text-sm text-[#8a8aa3]">{r.email}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-[#81B29A]">{r.referrals}</p>
                            <p className="text-xs text-[#8a8aa3]">referrals</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-[#8a8aa3] py-8">No referrals yet</p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </main>

      {/* Create Code Modal */}
      <Dialog open={showCreateCode} onOpenChange={setShowCreateCode}>
        <DialogContent className="bg-[#16162a] border-[#2a2a4a] text-white rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Fraunces, serif' }}>
              Create Invite Code
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCode} className="space-y-4">
            <div>
              <Label className="text-[#8a8aa3]">Code (optional - auto-generated if empty)</Label>
              <Input
                value={newCode.code}
                onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                placeholder="OT-SPRING24"
                className="bg-[#0f0f1a] border-[#2a2a4a] text-white rounded-xl mt-1"
              />
            </div>
            <div>
              <Label className="text-[#8a8aa3]">Tier</Label>
              <Select value={newCode.tier} onValueChange={(v) => setNewCode({ ...newCode, tier: v })}>
                <SelectTrigger className="bg-[#0f0f1a] border-[#2a2a4a] text-white rounded-xl mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#16162a] border-[#2a2a4a]">
                  <SelectItem value="standard" className="text-white">Standard (1 month free)</SelectItem>
                  <SelectItem value="premium" className="text-white">Premium (2 months free)</SelectItem>
                  <SelectItem value="vip" className="text-white">VIP (3 months free)</SelectItem>
                  <SelectItem value="seed" className="text-white">Seed (6 months free)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#8a8aa3]">Max Uses (empty = unlimited)</Label>
                <Input
                  type="number"
                  value={newCode.max_uses}
                  onChange={(e) => setNewCode({ ...newCode, max_uses: e.target.value })}
                  placeholder="100"
                  className="bg-[#0f0f1a] border-[#2a2a4a] text-white rounded-xl mt-1"
                />
              </div>
              <div>
                <Label className="text-[#8a8aa3]">Expires In (days)</Label>
                <Input
                  type="number"
                  value={newCode.expires_days}
                  onChange={(e) => setNewCode({ ...newCode, expires_days: e.target.value })}
                  className="bg-[#0f0f1a] border-[#2a2a4a] text-white rounded-xl mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-[#8a8aa3]">Description (optional)</Label>
              <Input
                value={newCode.description}
                onChange={(e) => setNewCode({ ...newCode, description: e.target.value })}
                placeholder="Spring promo for early adopters"
                className="bg-[#0f0f1a] border-[#2a2a4a] text-white rounded-xl mt-1"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowCreateCode(false)} className="flex-1 border-[#2a2a4a] text-white rounded-full">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full">
                Create Code
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Set Expiry Date Dialog */}
      <Dialog open={showExpiryDialog} onOpenChange={setShowExpiryDialog}>
        <DialogContent className="bg-[#16162a] border-[#2a2a4a] text-white rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Fraunces, serif' }}>
              Set Subscription Expiry
            </DialogTitle>
          </DialogHeader>
          {selectedUserForExpiry && (
            <div className="space-y-4">
              <div className="bg-[#0f0f1a] rounded-xl p-4">
                <p className="text-sm text-[#8a8aa3] mb-1">Setting expiry for:</p>
                <p className="font-medium">{selectedUserForExpiry.name}</p>
                <p className="text-sm text-[#8a8aa3]">{selectedUserForExpiry.email}</p>
                <Badge className={
                  selectedUserForExpiry.subscription_plan === "family" ? "bg-purple-500/20 text-purple-400 mt-2" :
                  selectedUserForExpiry.subscription_plan === "plus" ? "bg-[#E07A5F]/20 text-[#E07A5F] mt-2" :
                  "bg-gray-500/20 text-gray-400 mt-2"
                }>
                  {selectedUserForExpiry.subscription_plan || "free"}
                </Badge>
              </div>
              
              <div>
                <Label className="text-[#8a8aa3]">Expiry Date & Time</Label>
                <Input
                  type="datetime-local"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  className="bg-[#0f0f1a] border-[#2a2a4a] text-white rounded-xl mt-1"
                  data-testid="expiry-date-input"
                />
                <p className="text-xs text-[#8a8aa3] mt-1">
                  Current: {selectedUserForExpiry.subscription_end ? new Date(selectedUserForExpiry.subscription_end).toLocaleString() : "Not set"}
                </p>
              </div>
              
              <div>
                <Label className="text-[#8a8aa3]">Reason (optional, for audit log)</Label>
                <Input
                  value={expiryReason}
                  onChange={(e) => setExpiryReason(e.target.value)}
                  placeholder="Testing expiry flow"
                  className="bg-[#0f0f1a] border-[#2a2a4a] text-white rounded-xl mt-1"
                  data-testid="expiry-reason-input"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowExpiryDialog(false);
                    setSelectedUserForExpiry(null);
                    setExpiryDate("");
                    setExpiryReason("");
                  }} 
                  className="flex-1 border-[#2a2a4a] text-white rounded-full"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSetExpiry}
                  disabled={!expiryDate || isSettingExpiry}
                  className="flex-1 bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full"
                  data-testid="save-expiry-btn"
                >
                  {isSettingExpiry ? "Saving..." : "Set Expiry"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  );
};

export default AdminDashboard;
